import { forwardRef, useEffect, useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import classNames from "classnames";
import { useResourceContext } from "@/comn/features/context/ResourceContext";
import { useOptions_v2 } from "@/comn/hooks/useOptions.v2";

import { COMN_CODE } from "@/comn/constants";
import { comnUtils } from "../../utils";
import { Icon } from "../../components";
import { UseOptionsProps, useTheme } from "../../hooks";
import { BOLD_TEXT, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT } from "../../features/foundation";
import { v4 as uuid } from "uuid";

type SelectProps = UseOptionsProps & {
    edit?: boolean;
    all?: boolean;
    select?: boolean;
    viewType?: "label" | "value" | "both";
    editType?: "label" | "value" | "both";
    mode?: "edit" | "view" | null;

    name?: string;
    value?: any;
    readOnly?: boolean;
    disabled?: boolean;

    selectText?: string;
    allText?: string;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    onBlur?: (arg?: any) => void;
    onChange?: (...arg: any) => void;
    onClick?: (arg?: any) => void;
    callback?: (...args: any) => void;

    as?: "link";
    to?: string;
};

export const Select = forwardRef((props: SelectProps, ref: any) => {
    const { t } = useTranslation();
    const {
        /** */
        all,
        edit = true,
        mode,
        select = true,
        viewType = "both",
        editType = "label",

        filter,
        /** input props */
        name,
        value,
        readOnly,
        disabled,

        allText = t("L_AL"),
        selectText = t("L_SELT"),

        onBlur,
        onChange,
        onClick,
        callback,

        as,
        to,

        // For style
        color,
        bold,
        editColor,
        editBold,
        borderColor,
        fontSize,
        // For useOptions
        options: propsOptions,
        area,
        comnCd,
        excludes,
        includes,
    } = props;

    const _props = Object.fromEntries(
        Object.entries({
            name,
            readOnly,
            disabled,
            onBlur,
        }).filter(([, value]) => value !== undefined),
    );

    const navigate = useNavigate();

    const { data, options, findDataItem, findOptionsItem, getListItemKey } = useOptions_v2({
        area,
        comnCd,
        propsOptions,
        excludes,
        includes,
        filter,
    });

    const handleClick = () => {
        if (onClick) {
            onClick(value);
        }
    };

    const handleChange = (event: ChangeEvent<HTMLSelectElement>) => {
        const nextValue = event.target.value;
        if (onChange) {
            onChange(nextValue);
        }
        if (callback) {
            const dataItem = findDataItem(nextValue);
            const optionsItem = findOptionsItem(nextValue);
            callback({ data: dataItem, code: optionsItem?.value, label: optionsItem?.label });
        }
    };

    const hiddenInput = mode === "view" ? true : mode === "edit" ? false : !edit;
    const foramttedValue = formatSelect(value);
    const viewValue = t(viewSelect(value, { viewType, options }));

    return (
        <div className="w-full">
            {/* Text */}
            {hiddenInput &&
                (as === "link" ? (
                    <div
                        className="w-fit text-blue underline cursor-pointer"
                        title={viewSelect(value, { viewType, options }, true)}
                        onClick={() => {
                            handleClick();
                            if (to) navigate(to);
                        }}
                    >
                        {viewValue}
                    </div>
                ) : (
                    <div
                        className={classNames("w-fit", comnUtils.getViewStyle(color, bold, fontSize))}
                        title={viewSelect(value, { viewType, options }, true)}
                        onClick={handleClick}
                    >
                        {viewValue}
                    </div>
                ))}

            {/* Input */}
            <div hidden={hiddenInput}>
                <div className="relative flex w-full items-center">
                    <select
                        {..._props}
                        ref={ref}
                        value={foramttedValue}
                        onChange={handleChange}
                        onClick={handleClick}
                        className={classNames(
                            "input appearance-none pr-5",
                            readOnly && "pointer-events-none",
                            comnUtils.getEditStyle(editColor, editBold),
                            borderColor ? COLOR_BORDER[borderColor] : "",
                        )}
                    >
                        {(all || select) && <option value="">{all ? t(allText) : t(selectText)}</option>}
                        {options.map((item, index) => {
                            const { value, label } = item;
                            return (
                                <option key={getListItemKey(item, index)} value={value}>
                                    {editSelect(value, t(label), editType)}
                                </option>
                            );
                        })}
                    </select>
                    <Icon icon="down" size="xs" className="absolute right-[10px] pointer-events-none" />
                </div>
            </div>
        </div>
    );
});

export const viewSelect = (v: any, o?: any, all?: boolean) => {
    if (!o?.options) return;

    const option = o.options?.find(({ value }: any) => value === v);

    if (!option) return;

    const vt = option.value || "";
    const lt = option.label || "";
    const vtWithBracket = option.value ? `[${option.value}] ` : "";

    let view = o?.viewType === "value" ? vt : o?.viewType === "label" ? lt : vtWithBracket + lt;
    if (all === true) {
        view = vtWithBracket + lt;
    }

    return view;
};

export const editSelect = (value: string, label: string, editType: string) => {
    const view = editType === "label" ? label : editType === "value" ? value : "[" + value + "] " + label;
    return view;
};

export const formatSelect = (v: any) => {
    if (!v) return "";
    return String(v);
};

export const unformatSelect = (v: any) => {
    if (v) return undefined;
    return String(v);
};
